import { FC, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { ResourceExplorerAccountFilterLabel } from "./ResourceExplorerAccountFilterLabel";
import { FilterDropdownTrigger } from "../../../../FilterDropdownTrigger";
import { useAppSelector } from "../../../../../../../../../../store/hooks";
import { resourceExplorerPossibleFiltersByProviderSelector } from "../../../../../../../../../../store/resource-explorer/selectors/filters/possible-filters/resourceExplorerPossibleFiltersByProviderSelector";
import { resourceExplorerFilterByKeyAndProviderSelector } from "../../../../../../../../../../store/resource-explorer/selectors/filters/resourceExplorerFilterByKeyAndProviderSelector";
import {
  ResourceExplorerFilterItem,
  ProviderType,
} from "../../../../../../../../../../services/cloudchipr.api";
import { accountFilterChangeThunk } from "../../../../../../../../../../store/resource-explorer/thunks/filters/accountFilterChangeThunk";
import { getProviderName } from "../../../../../../../../../../utils/helpers/providers/getProviderName";
import { isDeepEqual } from "../../../../../../../../../../utils/is-deep-equal";
import { dbSelectedAccountSelector } from "../../../../../../../../../../store/resource-explorer/selectors/filters/selected/dbSelectedAccountSelector";
import { billingAccountDisabledReasonsByProviderSelector } from "../../../../../../../../../../store/common/selectors/billing-status/billingAccountDisabledReasonsByProviderSelector";
import { DropdownSelect } from "../../../../../../../../../common/select/dropdown-select/DropdownSelect";
import { isStringProviderType } from "../../../../../../../../../../utils/helpers/providers/isStringProviderType";
import { resourceExplorerAccountOptionFilterFn } from "../../../../../utils/helpers/resourceExplorerAccountOptionFilterFn";

interface ResourceExplorerAccountFilterProps {
  viewId: string;
  provider: ProviderType;
}
export const ResourceExplorerAccountFilter: FC<
  ResourceExplorerAccountFilterProps
> = ({ viewId, provider }) => {
  const dispatch = useDispatch();
  const filter = useAppSelector((state) =>
    resourceExplorerFilterByKeyAndProviderSelector(state, "account", provider),
  );

  const billingAccountDisablingReasons = useAppSelector((state) =>
    billingAccountDisabledReasonsByProviderSelector(state, provider),
  );
  const possibleFilters = useAppSelector((state) =>
    resourceExplorerPossibleFiltersByProviderSelector(state, provider),
  )?.account;

  const selectedFilter = useAppSelector((state) =>
    dbSelectedAccountSelector(state, provider),
  );

  const label = isStringProviderType(provider)
    ? getProviderName(provider, { capitalize: true, plural: true })
    : "";

  const options = useMemo(() => {
    return (
      possibleFilters?.values?.map((option) => {
        const disabledByReason = billingAccountDisablingReasons[option.value];

        return {
          value: option.value,
          listItemHeight: option.provider_account_id ? 55 : undefined,
          label: (
            <ResourceExplorerAccountFilterLabel
              title={option.title ?? option.value}
              accountId={option.provider_account_id}
            />
          ),
          rawValue: option,
          disabled: disabledByReason
            ? {
                type: disabledByReason.reasonType,
                reason: disabledByReason.reason,
              }
            : false,
        };
      }) ?? []
    );
  }, [possibleFilters, billingAccountDisablingReasons]);

  const submitHandlerOnClose = useCallback(
    (value: string[]) => {
      const newFilter: ResourceExplorerFilterItem = {
        ...filter,
        value,
        key: "account",
        operator: "in",
        cloud_provider: provider,
        include_values: filter?.include_values ?? null,
      };

      const isEqual = filter && isDeepEqual(newFilter, filter);

      if (!isEqual) {
        dispatch(accountFilterChangeThunk({ filter: newFilter, provider }));
      }
    },
    [dispatch, filter, provider],
  );

  const TriggerComponent = useCallback(
    (props: any) => (
      <FilterDropdownTrigger
        {...props}
        viewId={viewId}
        provider={provider}
        filterKey="account"
        title={label}
      />
    ),
    [label, provider, viewId],
  );

  return (
    <DropdownSelect
      label="Accounts"
      options={options}
      filterFn={resourceExplorerAccountOptionFilterFn}
      initialSelectedValues={(selectedFilter.value ?? []) as string[]}
      submitHandlerOnClose={submitHandlerOnClose}
      TriggerComponent={TriggerComponent}
      values={(selectedFilter.value ?? []) as string[]}
    />
  );
};
